import { useEffect, useRef, useState } from 'react';
import { NOTIFICATION_TONE, type NotificationRow } from 'shared';
import { api } from './api';
import { setFreshOrders } from './freshOrders';
import Icon from './Icon';
import { alert, haptic, notify } from './telegram';
import { useRealtime } from './realtime';
import { useScrollLock } from './useScrollLock';
import { useBackButton } from './useBackButton';

/** «5 минут назад» вместо даты: свежесть важнее точного времени. */
function ago(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;

  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;

  const d = Math.round(h / 24);
  if (d < 7) return `${d} дн назад`;

  return new Date(iso).toLocaleDateString('ru-RU');
}

interface Props {
  onOpenCargo: (id: string) => void;
  onOpenOrders: () => void;
}

/**
 * Колокольчик и лента событий.
 *
 * Счётчик обновляется по сокету, а сам список подтягивается только при
 * открытии: держать в памяти полсотни записей, которые смотрят раз в день,
 * незачем.
 */
export default function Notifications({ onOpenCargo, onOpenOrders }: Props) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // Общее правило приложения: под открытым слоем страница не двигается.
  useScrollLock(open);

  // Пока панель открыта — системная кнопка «назад» закрывает её.
  useBackButton(open, () => setOpen(false));

  // Закрытие по клику вне панели и по Escape. Затемняющей подложки нет:
  // это выпадающий список, а не модальное окно — она перекрывала шапку
  // и мешала уйти назад.
  useEffect(() => {
    if (!open) return;

    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    // capture, иначе клик по кнопке внутри панели успевает закрыть её раньше.
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /*
   * Один опрос на два значка.
   *
   * Сервер отдаёт разом и непрочитанные уведомления, и число
   * изменившихся рейсов: отдельный запрос ради второй цифры удвоил бы
   * фоновые обращения у каждой открытой вкладки, а спрашиваются они
   * одинаково часто.
   */
  useEffect(() => {
    api
      .unreadCount()
      .then((r) => {
        setUnread(r.unread);
        setFreshOrders(r.orders);
      })
      .catch(() => {});
  }, []);

  // Событие приходит в сокете — значок реагирует сразу, без перезагрузки.
  useRealtime('notification', () => {
    setUnread((n) => n + 1);
    // Короткий отклик вместо звука: в кабине телефон обычно в держателе,
    // и вибрация — единственное, что замечаешь, не отрывая глаз от дороги.
    notify('warning');
    if (open) void load();
  });

  async function load() {
    setLoading(true);
    try {
      const r = await api.notifications();
      setItems(r.items);
      setUnread(r.unread);
    } catch {
      // Молча: пустая лента понятнее сообщения об ошибке загрузки.
    } finally {
      setLoading(false);
    }
  }

  async function toggle() {
    haptic();
    const next = !open;
    setOpen(next);
    if (next) await load();
  }

  /**
   * Очистка ленты.
   *
   * Прочитанные уведомления копятся и мешают увидеть новое: лента на сорок
   * записей пролистывается дольше, чем читается одно свежее. Удаление
   * без подтверждения — терять тут нечего, событие уже произошло и
   * отражено в самой заявке.
   */
  async function clearAll() {
    haptic();
    try {
      await api.clearNotifications();
      setItems([]);
      setUnread(0);
    } catch {
      // Молча: не удалить уведомления — не та беда, о которой стоит
      // сообщать отдельным окном.
    }
  }

  async function markAll() {
    const r = await api.readNotifications();
    setUnread(r.unread);
    setItems((prev) =>
      prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })),
    );
  }

  async function openItem(n: NotificationRow) {
    if (!n.readAt) {
      await api.readNotifications([n.id]).then((r) => setUnread(r.unread));
    }
    setOpen(false);

    // Ведём к самой заявке, а не в список: событие про конкретный груз,
    // и «Рейсы» с двумя вкладками заставляют искать его заново. Карточка
    // открывается одинаково и для активной заявки, и для архивной.
    if (n.cargoId) {
      try {
        await api.cargo(n.cargoId);
        onOpenCargo(n.cargoId);
        return;
      } catch {
        // Заявку снесли или закрыли доступ — молчать здесь нельзя,
        // иначе нажатие выглядит как поломка.
        await alert('Заявка удалена или больше недоступна.');
        return;
      }
    }

    if (n.orderId) onOpenOrders();
  }

  return (
    <div className="bell-wrap" ref={box}>
      <button
        className={unread > 0 ? 'icon-btn icon-btn--on bell' : 'icon-btn bell'}
        onClick={() => void toggle()}
        aria-label={`Уведомления${unread > 0 ? `: ${unread} новых` : ''}`}
        title="Уведомления"
      >
        <Icon name="bell" size={17} />
        {unread > 0 && (
          <span className="bell__dot">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notes" role="dialog" aria-label="Уведомления">
            <div className="notes__head">
              <strong>Уведомления</strong>
              {unread > 0 && (
                <button className="chip chip--ghost" onClick={() => void markAll()}>
                  Прочитать все
                </button>
              )}
              {items.length > 0 && (
                <button
                  className="chip chip--ghost"
                  onClick={() => void clearAll()}
                  data-tip="Удалить все уведомления"
                >
                  Очистить
                </button>
              )}
            </div>

            {loading && <div className="meta" style={{ padding: 12 }}>Загружаю…</div>}

            {!loading && items.length === 0 && (
              <div className="empty">
                Пока тихо.
                <br />
                Здесь появятся отклики и события по вашим рейсам.
              </div>
            )}

          <div className="notes__list">
            {items.map((n) => (
                <button
                  key={n.id}
                  className={n.readAt ? 'note' : 'note note--new'}
                  onClick={() => void openItem(n)}
                >
                  <span className={`note__dot note__dot--${NOTIFICATION_TONE[n.kind]}`} />
                  <span className="note__text">
                    <span className="note__title">{n.title}</span>
                    {n.body && <span className="meta">{n.body}</span>}
                    <span className="note__time">{ago(n.createdAt)}</span>
                  </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
